// utils/sawtDat.js
//
// The one SAWT DAT builder. Every exporter selects and guards its own records,
// then hands them here; nothing else writes HSAWT / DSAWT / CSAWT lines.
//
// Format is pinned to the BIR-accepted sample (docs/SAWT_IMPLEMENTATION.md §1,
// fixture: utils/__fixtures__/12345678900001220251702.DAT):
//   • comma-delimited, CRLF line endings, AND a trailing CRLF after CSAWT
//   • SEQUENCE_NUM is an unpadded integer from 1
//   • quoting is positional: HSAWT quotes all four name fields; DSAWT quotes the
//     used name field(s) and leaves the unused ones bare (`"DD FOODS INC",,,`)
//   • codes, dates, rates and amounts are never quoted
//   • TAX_RATE is the plain percent (2% -> 2.00), never rate / 100
//   • CSAWT is exactly 7 fields; its totals equal the detail sums
//   • filename <TIN9><BC4><MMYYYY><FTYPE4>.DAT, FTYPE = filer entity (1701/1702)
//
// ⚠ Unconfirmed: the INDIVIDUAL payor layout. The sample has only corporate
// payors; individual rows emit `,"LAST","FIRST","MIDDLE"` by inference.
//
// Lives in the frontend on purpose: records, guards and the download all run in
// the browser, and there is no shared package with the CommonJS backend. Phase 3
// of SAWT_IMPLEMENTATION.md (backend service + aggregation) is still future work.

import { resolveFtypeCode, isCorporateFiler } from './sawtFormType';
import {
  cleanTIN, cleanDatName, splitIndividualName, toCentavos, formatCentavos, formatRatePercent,
  validPeriod, formatPeriod, formatFilenamePeriod, joinDatLines, splitDatLines, downloadDat,
} from './datCommon';

// Generic DAT helpers live in utils/datCommon.js (shared with the QAP builder
// to come); re-exported here so existing importers keep working.
export { cleanDatName, splitIndividualName };

/**
 * Re-parse a built file and confirm CSAWT carries exactly the sum of the DSAWT
 * amount columns. Names are cleaned of commas, so a plain split is exact.
 * @returns {string[]} blocking errors; empty when the control record ties out
 */
export function checkControlTotals(content) {
  const lines = splitDatLines(content);
  const details = lines.filter((l) => l.startsWith('DSAWT,'));
  const controls = lines.filter((l) => l.startsWith('CSAWT,'));

  if (!details.length) return ['The SAWT file has no detail rows.'];
  if (controls.length !== 1) return [`The SAWT file must have exactly one control record (found ${controls.length}).`];

  const control = controls[0].split(',');
  if (control.length !== 7) return [`The SAWT control record must have 7 fields (found ${control.length}).`];

  let base = 0;
  let withheld = 0;
  for (const line of details) {
    const f = line.split(',');
    if (f.length !== 15) return [`A SAWT detail row has ${f.length} fields instead of 15: ${line}`];
    base += toCentavos(f[13]);
    withheld += toCentavos(f[14]);
  }

  const errors = [];
  if (toCentavos(control[5]) !== base) {
    errors.push(`SAWT control total for income payments (${control[5]}) does not match the detail sum (${formatCentavos(base)}).`);
  }
  if (toCentavos(control[6]) !== withheld) {
    errors.push(`SAWT control total for tax withheld (${control[6]}) does not match the detail sum (${formatCentavos(withheld)}).`);
  }
  return errors;
}

/**
 * Build a SAWT DAT file.
 *
 * Inputs are passed through as the caller resolved them — this function does
 * not choose a period, an RDO or a branch. Anything required that is missing
 * blocks the file; nothing is defaulted.
 *
 * @param {object} args
 * @param {{tin:string, branchCode:string, rdoCode?:string, clientType:string, name:string}} args.filer
 * @param {{month:number, year:number|string}} args.period  header, CSAWT and filename period
 * @param {Array<{
 *   tin:string, branchCode:string, payeeEntity:'corporation'|'individual', name:string,
 *   period:{month:number, year:number|string}, atcCode:string, ratePercent:number,
 *   baseAmount:number, taxWithheld:number
 * }>} args.rows  in output order
 * @returns {{ok:true, content:string, filename:string} | {ok:false, errors:string[]}}
 */
export function buildSawtDat({ filer, period, rows }) {
  const errors = [];

  const filerTin = cleanTIN(filer?.tin);
  if (!filerTin) errors.push('Client TIN is required for DAT export.');

  const ftype = resolveFtypeCode(filer?.clientType);
  if (!ftype) errors.push('Client type (individual, corporation, OPC or partnership) is required for DAT export.');

  const filerBranch = String(filer?.branchCode ?? '');
  if (!/^\d{4}$/.test(filerBranch)) errors.push(`Client branch code must be 4 digits (got "${filerBranch}").`);

  if (!validPeriod(period)) errors.push('A filing month and year are required for DAT export.');

  const isCorpFiler = isCorporateFiler(filer?.clientType);
  const filerName = cleanDatName(filer?.name);
  const filerSplit = splitIndividualName(filer?.name);
  if (isCorpFiler ? !filerName : !filerSplit.last) errors.push('Client registered name is required for DAT export.');

  if (!Array.isArray(rows) || rows.length === 0) errors.push('There are no records to export.');

  const rowErrors = [];
  (rows || []).forEach((r, i) => {
    const who = cleanDatName(r?.name) || `row ${i + 1}`;
    const missing = [];
    if (!cleanTIN(r?.tin)) missing.push('TIN');
    if (!/^\d{4}$/.test(String(r?.branchCode ?? ''))) missing.push('4-digit branch code');
    if (r?.payeeEntity !== 'corporation' && r?.payeeEntity !== 'individual') missing.push('corporation/individual type');
    else if (r.payeeEntity === 'corporation' ? !cleanDatName(r.name) : !splitIndividualName(r.name).last) missing.push('name');
    if (!validPeriod(r?.period)) missing.push('month/year');
    if (!String(r?.atcCode ?? '').trim()) missing.push('ATC');
    if (!Number.isFinite(Number(r?.ratePercent)) || r?.ratePercent === null || r?.ratePercent === '') missing.push('tax rate');
    if (toCentavos(r?.baseAmount) === null) missing.push('income payment');
    if (toCentavos(r?.taxWithheld) === null) missing.push('tax withheld');
    if (missing.length) rowErrors.push(`${who}: missing ${missing.join(', ')}`);
  });
  if (rowErrors.length) {
    const shown = rowErrors.slice(0, 10);
    const rest = rowErrors.length - shown.length;
    errors.push(`Cannot build the SAWT file — ${shown.join('; ')}${rest > 0 ? `; and ${rest} more` : ''}.`);
  }

  if (errors.length) return { ok: false, errors };

  const periodStr = formatPeriod(period);
  const rdo = String(filer.rdoCode ?? '');

  const headerNames = isCorpFiler
    ? `"${filerName}","","",""`
    : `"","${filerSplit.last}","${filerSplit.first}","${filerSplit.middle}"`;
  const header = `HSAWT,H${ftype},${filerTin},${filerBranch},${headerNames},${periodStr},${rdo}`;

  let totalBase = 0;
  let totalWithheld = 0;
  const details = rows.map((r, i) => {
    const base = toCentavos(r.baseAmount);
    const withheld = toCentavos(r.taxWithheld);
    totalBase += base;
    totalWithheld += withheld;

    let names;
    if (r.payeeEntity === 'corporation') {
      names = `"${cleanDatName(r.name)}",,,`;
    } else {
      const n = splitIndividualName(r.name);
      names = `,"${n.last}","${n.first}","${n.middle}"`;
    }

    return [
      'DSAWT', `D${ftype}`, i + 1, cleanTIN(r.tin), String(r.branchCode),
      names, formatPeriod(r.period), '', String(r.atcCode).trim().toUpperCase(),
      formatRatePercent(r.ratePercent), formatCentavos(base), formatCentavos(withheld),
    ].join(',');
  });

  const control = `CSAWT,C${ftype},${filerTin},${filerBranch},${periodStr},${formatCentavos(totalBase)},${formatCentavos(totalWithheld)}`;
  const content = joinDatLines([header, ...details, control]);

  // The file is about to be filed — verify it rather than trust construction.
  const controlErrors = checkControlTotals(content);
  if (controlErrors.length) return { ok: false, errors: controlErrors };

  const filename = `${filerTin}${filerBranch}${formatFilenamePeriod(period)}${ftype}.DAT`;
  return { ok: true, content, filename };
}

/** Trigger the browser download for a built file. */
export function downloadSawtDat({ content, filename }) {
  downloadDat({ content, filename });
}
